import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { UnauthorizedError } from 'src/api/APIClient';
import { useAPI } from 'src/api/provider';
import { Button } from 'src/lib/components/Button';
import { Link as FormattedLink } from 'src/lib/components/Link';
import { TextField } from 'src/lib/components/fields/TextField';
import useForm from 'src/lib/components/form/useForm';
import Container from 'src/routes/Auth/components/Container';
import { z } from 'zod/mini';
import { ContactFooter } from '../components/Footer';
import { getMobileAuthContext, getMobileAuthPayload } from '../mobileAuthContext';
import { buildAuthInstrumentationHeaders } from '../authInstrumentationHeaders';

interface FormValues {
    username: string;
}

export const SingleSignOn = () => {
    const { t } = useTranslation('auth.sso');
    const { t: tValidation } = useTranslation('validation');
    const [searchParams] = useSearchParams();

    const api = useAPI();
    const { enqueueSnackbar } = useSnackbar();

    const form = useForm<FormValues>({
        validationSchema: z.object({
            username: z
                .string({
                    error: (iss) =>
                        iss.input === undefined ? tValidation('required') : tValidation('required'),
                })
                .check(z.minLength(1, tValidation('required'))),
        }),
        initialValues: {
            username: '',
        },
        onSubmit: async (values, { setErrors, setSubmitting }) => {
            // External auth flows (mobile, CLI) are handled centrally:
            // 1. Login.tsx stores params in sessionStorage
            // 2. App.tsx checks sessionStorage after auth and redirects
            // This keeps login method pages simple and avoids duplication.

            try {
                const mobileContext = getMobileAuthContext(searchParams);
                const mobilePayload = getMobileAuthPayload(mobileContext) || {};

                // check if username matches a saml based sso user
                const login_result = await api.post(
                    '/saml/login/',
                    {
                        username: values.username,
                        ...mobilePayload,
                    },
                    undefined,
                    buildAuthInstrumentationHeaders({
                        flow: 'saml_sso_precheck',
                        popupMode: 'redirect',
                        popupAttempted: false,
                        popupOpened: false,
                    }),
                );

                if (login_result.use_allauth && login_result.redirect_url) {
                    // Keep mobile session flags through SSO/allauth redirect loops.
                    // They are needed as fallback context if SAML returns to web login.
                    window.location.href = login_result.redirect_url;
                    return;
                }

                enqueueSnackbar('Single sign-on is currently unavailable.', {
                    variant: 'error',
                });
            } catch (err: any) {
                // Show error inline for both mobile and web users
                // This allows users to retry or switch to password/Google login
                if (err instanceof UnauthorizedError) {
                    setErrors({ username: err.message });
                } else {
                    enqueueSnackbar(err.message, { variant: 'error' });
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    const errors = form.submitCount > 0 ? form.errors : {};

    return (
        <>
            <Container width={360}>
                <form onSubmit={form.handleSubmit}>
                    <Box mb={4}>
                        <TextField
                            label={t('usernameLabel')}
                            name="username"
                            autoCapitalize="none"
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                            value={form.values.username}
                            onChange={form.handleChange}
                            error={errors.username}
                            autoFocus
                        />
                    </Box>
                    <Box mb={2}>
                        <Button
                            type="submit"
                            kind="filled"
                            color="primary"
                            fullWidth
                            disabled={form.isSubmitting}
                        >
                            {t('continueWithSSO')}
                        </Button>
                    </Box>
                    <Typography variant="body3" textAlign="center">
                        <FormattedLink to="/login">{t('loginWithPassword')}</FormattedLink>
                        {' | '}
                        <FormattedLink to="/auth/google">{t('loginWithGoogle')}</FormattedLink>
                    </Typography>
                </form>
            </Container>
            <ContactFooter />
        </>
    );
};
